/**
 * CurrencyFormModal — Create/edit currency with FormModal pattern.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Hash, Type, FileText, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import {
  useCreateCurrency,
  useUpdateCurrency,
} from '../hooks';
import type { Currency, CurrencyFormData } from '../types';

interface CurrencyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: Currency | null;
}

export function CurrencyFormModal({
  isOpen,
  onClose,
  editData,
}: CurrencyFormModalProps) {
  const { t } = useTranslation();
  const createMutation = useCreateCurrency();
  const updateMutation = useUpdateCurrency();
  const isEdit = !!editData;

  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: isEdit ? 'EDIT' : 'NEW',
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [symbol, setSymbol] = useState('');
  const [decimalPlaces, setDecimalPlaces] = useState(2);
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (editData) {
        setCode(editData.code);
        setName(editData.name);
        setNameEn(editData.nameEn || '');
        setSymbol(editData.symbol);
        setDecimalPlaces(editData.decimalPlaces);
        setIsDefault(editData.isDefault);
      } else {
        setCode('');
        setName('');
        setNameEn('');
        setSymbol('');
        setDecimalPlaces(2);
        setIsDefault(false);
      }
      setError('');
    }
  }, [isOpen, editData]);

  const isFormValid = useMemo(() => {
    return !!(code.trim() && name.trim() && symbol.trim());
  }, [code, name, symbol]);

  const validateForm = (): boolean => {
    setError('');
    if (!code.trim()) { setError(t('currencies.codeRequired')); return false; }
    if (!name.trim()) { setError(t('currencies.nameRequired')); return false; }
    if (!symbol.trim()) { setError(t('currencies.symbolRequired')); return false; }
    return true;
  };

  const handleSubmit = () => {
    const payload: CurrencyFormData = {
      code: code.trim().toUpperCase(),
      name: name.trim(),
      nameEn: nameEn.trim() || undefined,
      symbol: symbol.trim(),
      decimalPlaces,
      isDefault,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () =>
        isEdit
          ? updateMutation.mutateAsync({ id: editData!.id, data: payload })
          : createMutation.mutateAsync(payload),
      onSuccess: () => {
        toast.success(isEdit ? t('currencies.updateSuccess') : t('currencies.createSuccess'));
        onClose();
      },
      onError: (err: any) => {
        const msg = err.response?.data?.message || '';
        setError(msg || (isEdit ? t('currencies.updateError') : t('currencies.createError')));
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('currencies.edit') : t('currencies.create')}
      isPending={isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel={isEdit ? 'EDIT' : 'NEW'}
      error={error}
    >
      <div className="space-y-3">
        <FormField icon={<Hash className="h-4 w-4" />} label={t('currencies.code')} required>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={isEdit}
            placeholder={t('currencies.codePlaceholder')}
            maxLength={10}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('currencies.symbol')} required>
          <input
            type="text"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder={t('currencies.symbolPlaceholder')}
            maxLength={10}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('currencies.name')} required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('currencies.namePlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<Type className="h-4 w-4" />} label={t('currencies.nameEn')}>
          <input
            type="text"
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            placeholder={t('currencies.nameEnPlaceholder')}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>

        <FormField icon={<FileText className="h-4 w-4" />} label={t('currencies.decimalPlaces')}>
          <select
            value={decimalPlaces}
            onChange={(e) => setDecimalPlaces(Number(e.target.value))}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
            style={inputStyle}
          >
            {[0, 1, 2, 3, 4].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </FormField>

        <FormField icon={<Star className="h-4 w-4" />} label={t('currencies.isDefault')}>
          <div>
            <button
              type="button"
              onClick={() => setIsDefault(!isDefault)}
              className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer"
              style={{ backgroundColor: isDefault ? '#f59e0b' : MODAL_COLORS.border }}
            >
              <span
                className="inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform"
                style={{ transform: isDefault ? 'translateX(24px)' : 'translateX(4px)' }}
              />
            </button>
            {isDefault && (
              <p className="text-xs text-amber-600 mt-1">{t('currencies.defaultWarning')}</p>
            )}
          </div>
        </FormField>
      </div>
    </FormModal>
  );
}
